import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, signal, DestroyRef, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import * as ClientConstants from '../../../core/constants/client.constants';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { CalendarModule } from 'primeng/calendar';
import { FiltersService } from '../../../core/services/declarations/filters.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFloppyDisk, faPlus, faTrash, faUserCheck } from '@fortawesome/pro-solid-svg-icons';
import { CommonModule } from '@angular/common';
import { ClientState } from '../../../core/enums/client-state.enum';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import { Router } from '@angular/router';
import { navigateToRelatedClient } from '../../../core/utils/client-navigation.util';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';
import { ClientDeactivationModalComponent } from '../../client-deactivation-modal/client-deactivation-modal.component';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldAuditIndicatorComponent } from '../../field-audit-indicator/field-audit-indicator.component';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';

@Component({
  selector: 'app-identification-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule, 
    CheckboxModule, 
    CalendarModule, 
    InputNumberModule, 
    FontAwesomeModule, 
    CommonModule,
    ClientsTableComponent,
    ClientDeactivationModalComponent,
    InputTextareaModule,
    FieldAuditIndicatorComponent,
    AutoAuditDirective,
  ],
  templateUrl: './identification-form.component.html',
  styleUrl: './identification-form.component.scss'
})
export class IdentificationFormComponent implements OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  @Input() clientId: any;
  @Input() hasEditPermission: boolean;
  @Input() relationType: string | undefined;
  @Input() activeTab: string = 'identification';
  @Input() snapshotData: any | null = null;
  @Input() snapshotDocuments: any[] | null = null;
  @Input() snapshotRelatedPersons: any[] | null = null;
  /** When true, document/related-person changes stay in memory until parent saves (e.g. corporate operation wizard). */
  @Input() deferPersistence = false;
  /** Hides save / finalize / deactivation footer (wizard embed). */
  @Input() hidePersistenceUi = false;
  /** When set (wizard edit), applied after client load to overlay operation snapshot. */
  @Input() corporateOperationOverlay: Record<string, unknown> | null = null;

  @Output() createEvent = new EventEmitter<any>();
  @Output() formIsDirty = new EventEmitter<boolean>();
  @Output() stateChange = new EventEmitter<string>();
  /** Emits serialized operation payload when deferPersistence (corporate operation wizard). */
  @Output() corporateOperationPayloadChange = new EventEmitter<Record<string, unknown>>();

  faTrash = faTrash;
  faPlus = faPlus;
  faFloppyDisk = faFloppyDisk;
  faCheck = faUserCheck;

  public clientConstants = ClientConstants;
  public ClientState = ClientState;

  fieldAuditData: LatestPerFieldMap = {};

  responsibleUsers;

  identificationForm = this.fb.group({
    id: [],
    code: [],
    name: ['', Validators.required],
    firstSurname: [''],
    secondSurname: [''],
    codeApplication: [],
    application: [''],
    personType: [''],
    relationType: [''],
    companyType: [''],
    documentType: [''],
    documentNumber: [''],
    documentCaducityDate: [],
    representativeName: [''],
    representative: [],
    representativeNif: [
      '', 
      [
        (control) => {
          if (!control || !control.parent) return null;
          const docType = 'NIF';
          if (docType === 'NIF' && control.value) {
            const nifRegex = /^([0-9]{8})([-]?)([A-Za-z]{1})$/;
            return nifRegex.test(control.value) ? null : { invalidNif: true };
          }
          return null;
        }
      ]],
    comment: [''],
    constitutionDate: [],
    residenceCertificate: [''],
    responsible: [],
    startDate: [],
    endDate: [],
    socialCollaboratorAuthorization: [],
    applications: this.fb.array([]),
    state: [ClientState.Pending],
  });

  fixedApplications: any[] = [
    { name: 'A3Soc', code: '' },
    { name: 'A3Ren', code: '' },
    { name: 'A3Nom', code: '' },
    { name: 'A3Eco', code: '' },
    { name: 'A3Ges', code: '' },
  ];

  isAppModalVisible = false;
  isConfirmModalActive = false;
  isDocumentConfirmModalActive = false;
  isFinishClientModalActive = false;
  isCreating = false;
  isCreatingModal = false;
  editIndex = undefined;
  isLoading = false;
  showSaveButtons = false;

  client: any;

  relatedPersonsHeaders = [ 
    {
      name: 'Nombre',
      key: 'person'
    },
    {
      name: 'Documento de identidad',
      key: 'documentNumber'
    },
    {
      name: 'Fecha de nacimiento',
      key: 'constitutionDate'
    },
    {
      name: 'Tipo',
      key: 'type'
    },
    {
      name: 'Fecha de cónyuge',
      key: 'date'
    },
    {
      name: 'Acciones',
      key: 'delete'
    }
  ];

  configTable = {
    headers: this.relatedPersonsHeaders,
    data: [],
    isPersons: true
  }

  get activeRelatedPersonsTableConfig(): any {
    if (!this.snapshotRelatedPersons) return this.configTable;
    return {
      ...this.configTable,
      headers: this.configTable.headers.filter(
        (h: any) => h.key !== 'delete',
      ),
    };
  }

  get activeDocumentsTableConfig(): any {
    if (!this.snapshotDocuments) return this.configDocumentsTable;
    return {
      ...this.configDocumentsTable,
      headers: this.configDocumentsTable.headers.filter(
        (h: any) => h.key !== 'delete',
      ),
    };
  }

  documentHeaders = [ 
    {
      name: 'Tipo',
      key: 'type'
    },
    {
      name: 'Número',
      key: 'number'
    },
    {
      name: 'Fecha de caducidad',
      key: 'caducityDate'
    },
    {
      name: 'Principal',
      key: 'principal'
    },
    {
      name: 'Acciones',
      key: 'delete'
    }
  ];

  configDocumentsTable = {
    headers: this.documentHeaders,
    data: [],
    isDocument: true
  };


isModalActive: any;
isDocumentModalActive: any;
isSaved: boolean = false;
showDeactivationModal: boolean = false;
isUpdatingState = false;
isSavingClient = false;
isSubmittingModal = false;
/** Error de guardado o borrado de un modal (personas y documentos). */
submitError = '';
saveError = '';
private editingPersonIndex: number | undefined;
deactivationError = '';

modalForm: FormGroup = this.fb.group({
  id: [],
  client: [],
  person: [, Validators.required],
  type: [, Validators.required],
  spouseType: [],
  maritalStatus: [],
  isRegistered: [],
  date: [],


});

documentModalForm: FormGroup = this.fb.group({
  id: [],
  client: [],
  number: [
    '', 
    [
      (control) => {
        if (!control || !control.parent) return null;
        const docType = control.parent.get('type')?.value;
        if (docType === 'NIF' && control.value) {
          const nifRegex = /^([0-9]{8})([-]?)([A-Za-z]{1})$/;
          return nifRegex.test(control.value) ? null : { invalidNif: true };
        }
        return null;
      }, Validators.required
    ]],
  supportNumber: [],
  type: [, Validators.required],
  caducityDate: [],
  principal: [true]
});

relatedPersonsForm = this.fb.group({
  relatedPersons: this.fb.array([]),
});

documentsForm = this.fb.group({
  documents: this.fb.array([]),
});

  representativeOptions = signal<Array<{ id: number; name: string }>>([]);
  deleteIndex: number;
  deleteDocumentIndex: number;
  isSaving: boolean;

  principalExists: boolean = false;
  documentDuplicateError = false;
  isEditingPrincipalDocument: boolean = false;
  codeAlreadyExists: boolean = false;
  private editingDocumentIndex: number | undefined;

  constructor(
    private fb: FormBuilder,
    private filtersService: FiltersService,
    private clientsService: ClientsService,
    private router: Router,
    private auditService: AuditService,
    ) {
      this.identificationForm.valueChanges.subscribe(() => {
        if (this.identificationForm.dirty) {
          this.formIsDirty.emit(true);
        }
        if (this.deferPersistence) {
          this.emitCorporateOperationPayload();
        }
      });
    }

    ngOnChanges(changes: SimpleChanges) {
      // keep relationType input and form control in sync
      if (changes['relationType'] && changes['relationType'].currentValue !== undefined) {
        const current = changes['relationType'].currentValue;
        const ctrl = this.identificationForm.get('relationType');
        if (ctrl && current !== ctrl.value) {
          ctrl.setValue(current);
        }
      }

      if (changes['snapshotData']) {
        if (this.snapshotData) {
          this.identificationForm.patchValue(this.snapshotData, { emitEvent: false });
          this.identificationForm.disable({ emitEvent: false });
        } else if (!changes['snapshotData'].firstChange) {
          this.identificationForm.enable({ emitEvent: false });
          if (!this.hasEditPermission) {
            this.identificationForm.disable({ emitEvent: false });
          }
          void this.getData();
        }
      }

      if (changes['snapshotDocuments']) {
        if (this.snapshotDocuments) {
          const docs = this.snapshotDocuments.map((doc: any) => ({
            ...doc,
            caducityDate: parseDateOnlyToLocal(doc.caducityDate),
            client: this.clientId,
          }));
          this.configDocumentsTable = { ...this.configDocumentsTable, data: docs };
        } else if (!changes['snapshotDocuments'].firstChange) {
          void this.getData();
        }
      }

      if (changes['snapshotRelatedPersons']) {
        if (this.snapshotRelatedPersons) {
          const persons = this.snapshotRelatedPersons.map((rp: any) => ({
            ...rp,
            constitutionDate: rp.person?.constitutionDate,
            documentNumber: rp.documents?.number || '',
            client: this.clientId,
            person: typeof rp.person === 'object' && rp.person !== null ? rp.person.id : rp.person,
            date: parseDateOnlyToLocal(rp.date),
          }));
          this.configTable = { ...this.configTable, data: persons };
        } else if (!changes['snapshotRelatedPersons'].firstChange) {
          void this.getData();
        }
      }

      if (changes['corporateOperationOverlay']?.currentValue && this.client) {
        this.hydrateFromCorporateOperation(changes['corporateOperationOverlay'].currentValue);
      }
    }

     
  async ngOnInit() {
    this.isLoading = true;
    this.responsibleUsers = await this.filtersService.getUsers();
    try {
      const representatives = await this.clientsService.getClientsNames('PHYSICAL_PERSON');
      this.representativeOptions.set(representatives || []);
    } catch {
      this.representativeOptions.set([]);
    }

    this.identificationForm.get('representative')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((representativeValue) => {
        void this.autofillRepresentativeNif(this.normalizeRepresentativeId(representativeValue));
      });
    if (this.clientId) {
      this.isCreating = false;
    } else {
      this.isCreating = true;
    }
    if (this.isCreating) {
      const queryParams = this.router.parseUrl(this.router.url).queryParams;
      const personType = queryParams['personType'] || 'PHYSICAL_PERSON';
      this.identificationForm.get('personType')?.setValue(personType);
      this.identificationForm.get('relationType')?.setValue(queryParams['relationType'] || null);
      this.identificationForm.get('personType')?.disable();
    }
    await this.getData();
  }

  private async autofillRepresentativeNif(representativeId: number | null | undefined) {
    const nifControl = this.identificationForm.get('representativeNif');
    if (!nifControl) return;

    if (!representativeId) {
      nifControl.setValue('');
      return;
    }

    try {
      const documents = await this.clientsService.getClientDocuments(representativeId);
      const principalNif = (documents || []).find((doc: any) => doc?.principal && doc?.type === 'NIF');
      const nifDoc = principalNif || (documents || []).find((doc: any) => doc?.type === 'NIF');
      nifControl.setValue(nifDoc?.number || '');
    } catch {
      nifControl.setValue('');
    }
  }

  private normalizeRepresentativeId(value: any): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'object' && 'id' in value) return Number(value.id) || null;
    return Number(value) || null;
  }

  get applications(): FormArray<FormGroup> {
  return this.identificationForm.get('applications') as FormArray<FormGroup>;
  }

  get relatedPersons(): FormArray<FormGroup> {
    return this.relatedPersonsForm.get('relatedPersons') as FormArray<FormGroup>;
  }

  get documents(): FormArray<FormGroup> {
    return this.documentsForm.get('documents') as FormArray<FormGroup>;
  }
      
  async getData() {
    this.isLoading = true;
    if (this.clientId) {
      this.client = await this.clientsService.getClientById(this.clientId);
      if (this.client) {
        this.client.startDate = parseDateOnlyToLocal(this.client.startDate);
        this.client.endDate = parseDateOnlyToLocal(this.client.endDate);
        this.client.constitutionDate = parseDateOnlyToLocal(this.client.constitutionDate);
        this.client.documentCaducityDate = parseDateOnlyToLocal(this.client.documentCaducityDate);
        this.client.responsible = this.client.responsible ? this.client.responsible.id : null;
        // Coerce varchar → boolean for checkbox field (DB stores "true"/"false" strings)
        this.client.residenceCertificate = (this.client.residenceCertificate === true || this.client.residenceCertificate === 'true') as any;

        if (!this.snapshotData) {
          this.identificationForm.patchValue(this.client);
        }
        const representativeId = this.normalizeRepresentativeId(
          this.client?.representative?.id ?? this.client?.representative
        );
        if (representativeId) {
          this.identificationForm.get('representative')?.setValue(representativeId, { emitEvent: true });
        }
        this.applications.clear();

        if(this.client.applications){
          this.fixedApplications.forEach((fixedApp) => {
            const appFromBackend = this.client.applications.find(
              (backendApp: any) => backendApp.name === fixedApp.name
            );
            this.applications.push(
              this.createApplication(
                fixedApp.name,
                appFromBackend ? appFromBackend.code : ''
              )
            );
          });
        } 

        if (!this.hasEditPermission) {
          this.identificationForm.disable({ emitEvent: true });
        }

        if (!this.snapshotRelatedPersons) {
          const relatedPersons = await this.clientsService.getClientRelatedPersons(this.clientId);
          if (relatedPersons) {
            relatedPersons.forEach((relatedPerson: any) => {
              relatedPerson.constitutionDate = relatedPerson.person?.constitutionDate;
              relatedPerson.documentNumber = relatedPerson.documents?.number || '';
              relatedPerson.client = this.clientId;
              relatedPerson.person = typeof relatedPerson.person === 'object' && relatedPerson.person !== null
                ? relatedPerson.person.id
                : relatedPerson.person;
              relatedPerson.date = parseDateOnlyToLocal(relatedPerson.date);
            });
            this.configTable.data = relatedPersons;
          }
        }

        if (!this.snapshotDocuments) {
          const documents = await this.clientsService.getClientDocuments(this.clientId);
          if (documents) {
            documents.forEach((document: any) => {
              document.caducityDate = parseDateOnlyToLocal(document.caducityDate);
              document.client = this.clientId;
            });
            this.configDocumentsTable.data = documents;
          }
        }
        if (this.clientId) {
          try {
            this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client');
          } catch {
            this.fieldAuditData = {};
          }
        }

        if (this.corporateOperationOverlay) {
          this.hydrateFromCorporateOperation(this.corporateOperationOverlay);
        }

        this.emitCorporateOperationPayload();

      } 
    } else {
      this.fixedApplications.forEach((app) => {
        this.applications.push(this.createApplication(app.name, ''));
      });
    }
    this.isLoading = false;
  }

  initApplications() {
    if (this.applications.length === 0) {
      this.fixedApplications.forEach(app => {
        this.applications.push(this.createApplication(app.name, app.code));
      });
    }
  }

  createApplication(name: string, code: string): FormGroup {
    return this.fb.group({
      name: [name],
      code: [code],
    });
  }

  async onCodeBlur() {
    const code = this.identificationForm.get('code')?.value;
    if (!code) {
      this.codeAlreadyExists = false;
      return;
    }
    try {
      const clientId = this.identificationForm.get('id')?.value;
      const result = await this.clientsService.checkCodeAvailability(code, clientId ?? undefined);
      this.codeAlreadyExists = !result.available;
    } catch {
      this.codeAlreadyExists = false;
    }
  }

  /** Guarda la identificación. Devuelve true si todo se guardó; si no, deja el motivo en saveError. */
  async save(): Promise<boolean> {
    if (this.isSavingClient) return false; // doble clic = clientes duplicados
    this.saveError = '';
    if (this.codeAlreadyExists) {
      this.saveError = 'El código de cliente ya está en uso.';
      return false;
    }

    this.identificationForm.value.applications = this.identificationForm.value.applications.map((app: any) => {
      return {
        name: app.name,
        code: Number(app.code) || null,
      };
    });

    let client = this.identificationForm.getRawValue();
    let response;
    client.startDate = toDateOnlyPayload(client.startDate);
    client.endDate = toDateOnlyPayload(client.endDate);
    client.constitutionDate = toDateOnlyPayload(client.constitutionDate);
    client.documentCaducityDate = toDateOnlyPayload(client.documentCaducityDate);
    if (client.name) {
      client.name = client.name.toUpperCase();
    }
    if (client.firstSurname) {
      client.firstSurname = client.firstSurname.toUpperCase();
    }
    if (client.secondSurname) {
      client.secondSurname = client.secondSurname.toUpperCase();
    }
    if (client.representativeNif) {
      client.representativeNif = client.representativeNif.toUpperCase();
    }
    this.isSavingClient = true;
    try {
      if (this.isCreating) {
        response = await this.clientsService.saveClientIdentification(client);
        const clientId = response.id;
        // Si lo que sigue falla, el reintento debe actualizar este cliente y no crear otro.
        this.identificationForm.patchValue({ id: clientId });
        // Save related persons/documents sequentially to avoid race conditions.
        // Lo ya guardado se quita de la lista para no duplicarlo en un reintento.
        for (const relatedPerson of [...this.configTable.data]) {
          relatedPerson.client = clientId;
          await this.clientsService.saveClientRelatedPerson({
            ...relatedPerson,
            date: toDateOnlyPayload(relatedPerson.date),
          });
          this.configTable = { ...this.configTable, data: this.configTable.data.filter((p: any) => p !== relatedPerson) };
        }
        for (const document of [...this.configDocumentsTable.data]) {
          document.client = clientId;
          await this.clientsService.uploadClientDocument(this.toDocumentPayload(document));
          this.configDocumentsTable = { ...this.configDocumentsTable, data: this.configDocumentsTable.data.filter((d: any) => d !== document) };
        }
        this.isSaved = true;
        setTimeout(() => this.isSaved = false, 3000);
        // Emit event here so parent can update URL/title
        if (!this.clientId && response && response.id) {
          this.createEvent.emit(response.id);
        }
      } else {
        response = await this.clientsService.saveClientIdentification(client);
        this.isSaved = true;
        setTimeout(() => this.isSaved = false, 3000);
        await this.getData();
      }
      return true;
    } catch (e) {
      console.error('Error al guardar el cliente:', e);
      this.saveError = (e as any)?.status === 409
        ? 'El código de cliente ya está en uso.'
        : 'No se pudo guardar el cliente. Revisa los datos e inténtalo de nuevo.';
      return false;
    } finally {
      this.isSavingClient = false;
    }
  }

  async completeClient(): Promise<boolean> {
    const previousState = this.identificationForm.get('state')?.value;
    this.identificationForm.patchValue({
      state: ClientState.Active,
    });
    const saved = await this.save();
    if (!saved) {
      // No quedó guardado como activo: no mostrar el formulario como si lo estuviera.
      this.identificationForm.patchValue({ state: previousState });
      return false;
    }
    await this.router.navigate(['/clients']);
    return true;
  }

  async saveClientAsDraft() {
    this.identificationForm.patchValue({
      state: ClientState.Pending,
    });
    await this.save();
  }

  openFormModal(index?: number) {
    this.submitError = '';
    this.isModalActive = true;
    this.modalForm.enable({ emitEvent: false }); // puede venir deshabilitado del modo histórico
    this.editingPersonIndex = index;
    if (index === undefined) {
      this.isCreatingModal = true;
      this.modalForm.reset();
    } else {
      this.isCreatingModal = false;
      const relatedPerson = this.configTable.data.at(index);
      this.modalForm.patchValue(relatedPerson);
      if (this.snapshotData) {
        this.modalForm.disable();
      } else if(!this.hasEditPermission) {
        Object.keys(this.modalForm.controls).forEach((control) => {
          this.modalForm.get(control)?.disable({ emitEvent: false });
        });
      }
    }
  }

  openDocumentFormModal(index?: number) {
    this.submitError = '';
    this.isDocumentModalActive = true;
    this.documentModalForm.enable({ emitEvent: false }); // puede venir deshabilitado del modo histórico
    this.documentDuplicateError = false;
    const principalDoc = this.configDocumentsTable.data.find((doc: any) => doc.principal);
    this.principalExists = !!principalDoc;
    if (index === undefined) {
      this.isCreatingModal = true;
      this.editingDocumentIndex = undefined;
      this.documentModalForm.reset({
        principal: false
      });
      this.isEditingPrincipalDocument = false;
    } else {
      this.isCreatingModal = false;
      this.editingDocumentIndex = index;
      const document = this.configDocumentsTable.data.at(index);
      this.documentModalForm.patchValue(document);
      this.isEditingPrincipalDocument = !!document.principal;
      if (this.snapshotData) {
        this.documentModalForm.disable();
      } else if(!this.hasEditPermission) {
        Object.keys(this.documentModalForm.controls).forEach((control) => {
          this.documentModalForm.get(control)?.disable({ emitEvent: false });
        });
      }
    }
  }

  async saveItem() {
    if (this.isSubmittingModal) return; // doble clic = duplicados
    this.isSubmittingModal = true;
    this.submitError = '';
    try {
      await this.doSaveItem();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmittingModal = false;
    }
  }

  private async doSaveItem() {
    let relatedPerson = this.modalForm.value;
    relatedPerson.client = this.clientId;
    // relatedPerson.person = relatedPerson.person.id;
    if (!this.clientId) {
      // Alta nueva: las personas se guardan al crear el cliente, aquí solo se añaden o editan en memoria.
      const data = [...this.configTable.data];
      if (this.editingPersonIndex !== undefined) {
        data[this.editingPersonIndex] = { ...data[this.editingPersonIndex], ...relatedPerson };
      } else {
        data.push(relatedPerson);
      }
      this.configTable = { ...this.configTable, data };
    } else {
      await this.clientsService.saveClientRelatedPerson({
        ...relatedPerson,
        date: toDateOnlyPayload(relatedPerson.date),
      });
      await this.getData();
    }
    this.modalForm.reset();
    this.isModalActive = false;
  }


  private toDocumentPayload(document: any) {
    return {
      ...document,
      caducityDate: toDateOnlyPayload(document.caducityDate),
    };
  }

  async saveDocument() {
    if (this.isSubmittingModal) return; // doble clic = duplicados
    this.isSubmittingModal = true;
    this.submitError = '';
    try {
      await this.doSaveDocument();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmittingModal = false;
    }
  }

  private async doSaveDocument() {
    let document = this.documentModalForm.getRawValue();
    document.client = this.clientId;
    document.number = document.number.toUpperCase();
    if (document.supportNumber) {
      document.supportNumber = document.supportNumber.toUpperCase();
    }

    const repetido = this.configDocumentsTable.data.some((doc: any, i: number) =>
      i !== this.editingDocumentIndex && (document.id == null || doc.id !== document.id)
      && doc.type === document.type && String(doc.number ?? '').toUpperCase() === document.number);
    this.documentDuplicateError = repetido;
    if (repetido) return;

    // ENFORCE ONLY ONE PRINCIPAL DOCUMENT
    if (document.principal) {
      if (this.deferPersistence || !this.clientId) {
        this.configDocumentsTable.data.forEach((doc, i) => {
          if (i !== this.editingDocumentIndex) {
            doc.principal = false;
          }
        });
      } else {
        const docs = await this.clientsService.getClientDocuments(this.clientId);
        for (const doc of docs) {
          if (doc.id !== document.id && doc.principal) {
            doc.principal = false;
            await this.clientsService.updateClientDocument(doc.id, doc);
          }
        }
      }
    }

    if (this.deferPersistence) {
      const data = [...this.configDocumentsTable.data];
      if (this.isCreatingModal) {
        data.push({ ...document, client: this.clientId });
      } else if (this.editingDocumentIndex !== undefined) {
        const prev = data[this.editingDocumentIndex] ?? {};
        data[this.editingDocumentIndex] = { ...prev, ...document, client: this.clientId };
      }
      this.configDocumentsTable = { ...this.configDocumentsTable, data };
    } else if (!this.clientId) {
      // Alta nueva: el documento se sube al crear el cliente.
      const data = [...this.configDocumentsTable.data];
      if (this.editingDocumentIndex !== undefined) {
        data[this.editingDocumentIndex] = { ...data[this.editingDocumentIndex], ...document };
      } else {
        data.push(document);
      }
      this.configDocumentsTable = { ...this.configDocumentsTable, data };
    } else if (!this.isCreatingModal && document.id) {
      await this.clientsService.updateClientDocument(document.id, this.toDocumentPayload(document));
      await this.getData();
    } else {
      await this.clientsService.uploadClientDocument(this.toDocumentPayload(document));
      await this.getData();
    }
    this.documentModalForm.reset({
      principal: false
    });
    this.editingDocumentIndex = undefined;
    this.isDocumentModalActive = false;
    this.emitCorporateOperationPayload();
  }

  async deleteItem(index: number) {
    if (!this.clientId) {
      this.configTable = { ...this.configTable, data: this.configTable.data.filter((_: any, i: number) => i !== index) };
      return;
    }
    await this.clientsService.deleteClientRelatedPerson(this.configTable.data.at(index).id);
    await this.getData();
  }


  async deleteDocument(index: number) {
    if (this.deferPersistence || !this.clientId) {
      const data = this.configDocumentsTable.data.filter((_, i) => i !== index);
      this.configDocumentsTable = { ...this.configDocumentsTable, data };
      return;
    }
    await this.clientsService.deleteClientDocument(this.configDocumentsTable.data.at(index).id);
    await this.getData();
  }

  /** Payload for corporate operation `operationData` (transformación / identificación). */
  serializeForCorporateOperation(): Record<string, unknown> {
    const raw = this.identificationForm.getRawValue() as Record<string, unknown>;
    const residenceCertificate =
      raw['residenceCertificate'] === true || raw['residenceCertificate'] === 'true';

    const documents = (this.configDocumentsTable.data ?? []).map((doc: any) => ({
      id: doc.id ?? undefined,
      type: doc.type || undefined,
      number: doc.number || undefined,
      supportNumber: doc.supportNumber || undefined,
      caducityDate: toDateOnlyPayload(doc.caducityDate) || undefined,
      principal: !!doc.principal,
    }));

    return {
      name: raw['name'] ? String(raw['name']).trim() || undefined : undefined,
      code: raw['code'] ?? undefined,
      codeApplication: raw['codeApplication'] ?? undefined,
      companyType: raw['companyType'] || undefined,
      residenceCertificate,
      comment: raw['comment'] || undefined,
      documents,
    };
  }

  emitCorporateOperationPayload(): void {
    if (!this.deferPersistence) return;
    this.corporateOperationPayloadChange.emit(this.serializeForCorporateOperation());
  }

  /** Restore wizard state from saved operation data (edit mode). */
  hydrateFromCorporateOperation(data: Record<string, unknown> | null | undefined): void {
    if (!data) return;

    const patch: Record<string, unknown> = {};
    if (data['name'] !== undefined) patch['name'] = data['name'];
    if (data['code'] !== undefined) patch['code'] = data['code'];
    if (data['codeApplication'] !== undefined) patch['codeApplication'] = data['codeApplication'];
    if (data['companyType'] !== undefined) patch['companyType'] = data['companyType'];
    if (data['comment'] !== undefined) patch['comment'] = data['comment'];
    if (data['residenceCertificate'] !== undefined) {
      patch['residenceCertificate'] =
        data['residenceCertificate'] === true || data['residenceCertificate'] === 'true';
    }
    if (Object.keys(patch).length > 0) {
      this.identificationForm.patchValue(patch, { emitEvent: false });
    }

    const docs = Array.isArray(data['documents']) ? data['documents'] : [];
    if (docs.length > 0) {
      const mapped = docs.map((doc: any) => ({
        ...doc,
        caducityDate: parseDateOnlyToLocal(doc.caducityDate),
        client: this.clientId,
      }));
      this.configDocumentsTable = { ...this.configDocumentsTable, data: mapped };
    }
    this.emitCorporateOperationPayload();
  }

  openConfirmModal(index: number) {
    this.submitError = '';
    this.submitError = '';
    this.deleteIndex = index;
    this.isConfirmModalActive = true;
  }

  openDocumentConfirmModal(index: number) {
    this.submitError = '';
    this.submitError = '';
    this.deleteDocumentIndex = index;
    this.isDocumentConfirmModalActive = true;
  }

  onDocumentCloseConfirmModal() {
    this.submitError = '';
    this.deleteDocumentIndex = undefined;
    this.isDocumentConfirmModalActive = false;
  }

  onCloseConfirmModal() {
    this.submitError = '';
    this.deleteIndex = undefined;
    this.isConfirmModalActive = false;
  }

  async onConfirmDelete() {
    if (this.isSubmittingModal) return;
    this.isSubmittingModal = true;
    this.submitError = '';
    try {
      await this.doOnConfirmDelete();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    } finally {
      this.isSubmittingModal = false;
    }
  }

  private async doOnConfirmDelete() {
    await this.deleteItem(this.deleteIndex);
    this.isConfirmModalActive = false;
    this.deleteIndex = undefined;
  }

  async onConfirmDocumentDelete() {
    if (this.isSubmittingModal) return;
    this.isSubmittingModal = true;
    this.submitError = '';
    try {
      await this.doOnConfirmDocumentDelete();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    } finally {
      this.isSubmittingModal = false;
    }
  }

  private async doOnConfirmDocumentDelete() {
    await this.deleteDocument(this.deleteDocumentIndex);
    this.isDocumentConfirmModalActive = false;
    this.deleteDocumentIndex = undefined;
    this.emitCorporateOperationPayload();
  }

  openFinishClientModal() {
    this.submitError = '';
    this.isFinishClientModalActive = true;
  }

  onCloseFinishClientModal() {
    this.isFinishClientModalActive = false;
  }

  async onFinishClient() {
    this.isSaving = true;
    try {
      await this.completeClient();
    } finally {
      this.isFinishClientModalActive = false;
      this.isSaving = false;
    }
  }

goToRelatedPersonDetail(index: any) {
  const person = this.configTable.data[index];
  const personId = person?.person;
  if (personId) {
    navigateToRelatedClient(this.router, personId, this.activeTab);
  }
}

openDeactivationModal() {
  this.deactivationError = '';
  this.showDeactivationModal = true;
}

closeDeactivationModal() {
  this.showDeactivationModal = false;
}

async onDeactivationConfirm(endDate: Date | null) {
  if (this.isUpdatingState) return; // doble clic = dos POST con auditoría
  this.isUpdatingState = true;
  this.deactivationError = '';
  try {
    const isReactivation = endDate === null;
    const newState = isReactivation ? 'ACTIVE' : 'INACTIVE';
    
    await this.clientsService.updateClientState(this.clientId, newState, endDate);
    await this.getData();
    this.stateChange.emit(newState);
    this.showDeactivationModal = false;
    this.isSaved = true;
    setTimeout(() => this.isSaved = false, 3000);
  } catch (error) {
    console.error('Error al actualizar estado del cliente:', error);
    this.deactivationError = 'No se pudo actualizar el estado del cliente. Inténtalo de nuevo.';
  } finally {
    this.isUpdatingState = false;
  }
}

}
